import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaHome, FaBriefcase, FaDollarSign, FaCheck, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import ThankYouModal from '../components/ThankYouModal';

const FinanceApplication = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  
  // Define consistent input styling
  const inputClass = "w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none placeholder-gray-400";
  const labelClass = "block text-sm font-medium text-gray-300 mb-2";
  const headingClass = "text-2xl font-semibold text-white mb-6";
  
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    ssn: '',
    driversLicense: '',
    
    // Address Information
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    timeAtAddress: '',
    rentOrOwn: 'rent',
    monthlyPayment: '',
    
    // Previous Address (if less than 1 years)
    previousStreetAddress: '',
    previousCity: '',
    previousState: '',
    previousZipCode: '',
    previousTimeAtAddress: '',
    
    // Employment Information
    employerName: '',
    employerPhone: '',
    jobTitle: '',
    timeEmployed: '',
    monthlyIncome: '',
    additionalIncome: '',
    incomeSource: '',
    
    // Vehicle & Loan Information
    vehicleInterest: '',
    downPayment: '',
    tradeIn: false,
    tradeInYear: '',
    tradeInMake: '',
    tradeInModel: '',
    tradeInMileage: '',
    tradeInPayoff: '',
    
    // References
    reference1Name: '',
    reference1Phone: '',
    reference1Relationship: '',
    reference2Name: '',
    reference2Phone: '',
    reference2Relationship: '',
    
    // Consent
    creditCheckConsent: false,
    termsAccepted: false
  });

  const steps = [
    { number: 1, title: 'Personal Information', icon: FaUser },
    { number: 2, title: 'Address', icon: FaHome },
    { number: 3, title: 'Employment', icon: FaBriefcase },
    { number: 4, title: 'Vehicle & Loan', icon: FaDollarSign },
    { number: 5, title: 'Review & Submit', icon: FaCheck }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Map form fields (camelCase) to database columns (snake_case)
      const databaseData = {
        // Personal Information
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        date_of_birth: formData.dateOfBirth || null,
        ssn: formData.ssn || '', // Store full SSN for manual credit checks with 700Credit
        drivers_license: formData.driversLicense || '', // Store full DL for credit verification
        
        // Address Information
        street_address: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        time_at_address: formData.timeAtAddress,
        rent_or_own: formData.rentOrOwn,
        monthly_payment: parseFloat(formData.monthlyPayment) || null,
        
        // Previous Address (if applicable)
        previous_street_address: formData.previousStreetAddress || null,
        previous_city: formData.previousCity || null,
        previous_state: formData.previousState || null,
        previous_zip_code: formData.previousZipCode || null,
        previous_time_at_address: formData.previousTimeAtAddress || null,
        
        // Employment Information
        employer_name: formData.employerName,
        employer_phone: formData.employerPhone,
        job_title: formData.jobTitle,
        time_employed: formData.timeEmployed,
        monthly_income: parseFloat(formData.monthlyIncome) || null,
        additional_income: parseFloat(formData.additionalIncome) || null,
        income_source: formData.incomeSource,
        
        // Vehicle & Loan Information
        vehicle_interest: formData.vehicleInterest,
        down_payment: parseFloat(formData.downPayment) || null,
        trade_in: formData.tradeIn,
        trade_in_year: formData.tradeInYear ? parseInt(formData.tradeInYear) : null,
        trade_in_make: formData.tradeInMake,
        trade_in_model: formData.tradeInModel,
        trade_in_mileage: formData.tradeInMileage ? parseInt(formData.tradeInMileage) : null,
        trade_in_payoff: parseFloat(formData.tradeInPayoff) || null,
        
        // References
        reference1_name: formData.reference1Name,
        reference1_phone: formData.reference1Phone,
        reference1_relationship: formData.reference1Relationship,
        reference2_name: formData.reference2Name,
        reference2_phone: formData.reference2Phone,
        reference2_relationship: formData.reference2Relationship,
        
        // Consent & Status
        credit_check_consent: formData.creditCheckConsent,
        terms_accepted: formData.termsAccepted,
        status: 'pending',
        created_at: new Date().toISOString()
      };
      
      console.log('Submitting application with data:', databaseData);
      
      // Save to database
      const { data, error } = await supabase
        .from('finance_applications')
        .insert([databaseData]);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      // Send email notification to owner
      try {
        const emailjs = (await import('emailjs-com')).default;
        
        // Initialize EmailJS
        emailjs.init('fO2OoJPQ4GZrkJp7N'); // Your EmailJS public key
        
        const templateParams = {
          to_email: 'wayne@paradise-performance.com',
          from_name: `${formData.firstName} ${formData.lastName}`,
          from_email: formData.email || 'Not provided',
          from_phone: formData.phone || 'Not provided',
          message: `
            NEW FINANCE APPLICATION RECEIVED!
            
            Applicant: ${formData.firstName} ${formData.lastName}
            Email: ${formData.email || 'Not provided'}
            Phone: ${formData.phone || 'Not provided'}
            
            Employment: ${formData.employerName || 'Not provided'}
            Monthly Income: $${formData.monthlyIncome || 'Not provided'}
            
            Vehicle Interest: General Inquiry
            Down Payment: $${formData.downPayment || '0'}
            
            SSN: ${formData.ssn ? `***-**-${formData.ssn.slice(-4)}` : 'Not provided'}
            
            Please check the admin panel for full details.
          `,
          subject: `New Finance Application - ${formData.firstName} ${formData.lastName}`
        };
        
        await emailjs.send(
          'service_i57i9hh',  // Your EmailJS service ID
          'template_99xkhdd', // Your EmailJS template ID
          templateParams
        );
        
        console.log('✅ Email notification sent to owner');
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't block the submission if email fails
      }

      // Show success modal with conversion tracking
      setShowThankYou(true);
    } catch (error) {
      console.error('Error submitting application:', error);
      
      // More detailed error message
      let errorMessage = 'There was an error submitting your application. ';
      if (error.message?.includes('relation') || error.message?.includes('table')) {
        errorMessage += 'Database configuration issue. Please contact us directly at (817) 382-2316.';
      } else {
        errorMessage += 'Please try again or contact us directly at (817) 382-2316.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
    navigate('/inventory');
  };

  return (
    <>
      {/* Thank You Modal with Google Ads Conversion Tracking */}
      <ThankYouModal
        isOpen={showThankYou}
        onClose={handleThankYouClose}
        title="Application Submitted!"
        message="Your finance application has been submitted successfully! We will contact you within 24 hours."
        autoClose={true}
        autoCloseDelay={6000}
      />
      
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 pt-24">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2 pt-8">Finance Application</h1>
          <p className="text-blue-400">Get pre-approved in minutes</p>
        </motion.div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <div key={step.number} className="flex-1 relative">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep >= step.number
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50'
                        : 'bg-gray-700 text-gray-300 border border-gray-600'
                    }`}
                  >
                    {currentStep > step.number ? '✓' : step.number}
                  </div>
                  <span className="text-xs mt-2 text-center text-gray-300 hidden sm:block">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`absolute top-6 left-1/2 w-full h-0.5 transition-colors ${
                      currentStep > step.number ? 'bg-blue-500' : 'bg-gray-600'
                    }`}
                    style={{ transform: 'translateX(50%)' }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl p-8"
        >
          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className={headingClass}>Personal Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Phone *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.dateOfBirth}
                      onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Social Security Number *
                    </label>
                    <input
                      type="text"
                      required
                      pattern="[0-9]{3}-[0-9]{2}-[0-9]{4}"
                      placeholder="XXX-XX-XXXX"
                      value={formData.ssn}
                      onChange={(e) => handleInputChange('ssn', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Driver's License # *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.driversLicense}
                      onChange={(e) => handleInputChange('driversLicense', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Address Information */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className={headingClass}>Address Information</h2>
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>
                      Street Address *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.streetAddress}
                      onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className={labelClass}>
                        City *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        State *
                      </label>
                      <input
                        type="text"
                        required
                        maxLength="2"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        className={inputClass}
                        placeholder="TX"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className={labelClass}>
                        Time at Address (Years) *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        required
                        value={formData.timeAtAddress}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Only allow numbers and decimals
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleInputChange('timeAtAddress', value);
                          }
                        }}
                        className={inputClass}
                        placeholder="e.g., 2.5"
                      />
                      <p className="text-xs text-gray-400 mt-1">Enter number of years (e.g., 1, 1.5, 2)</p>
                    </div>
                    <div>
                      <label className={labelClass}>
                        Rent or Own *
                      </label>
                      <select
                        required
                        value={formData.rentOrOwn}
                        onChange={(e) => handleInputChange('rentOrOwn', e.target.value)}
                        className={inputClass}
                      >
                        <option value="rent">Rent</option>
                        <option value="own">Own</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>
                        Monthly Payment
                      </label>
                      <input
                        type="number"
                        value={formData.monthlyPayment}
                        onChange={(e) => handleInputChange('monthlyPayment', e.target.value)}
                        className={inputClass}
                        placeholder="$"
                      />
                    </div>
                  </div>

                  {/* Previous Address - Only show if time at address < 1 years */}
                  {formData.timeAtAddress && parseFloat(formData.timeAtAddress) < 2 && (
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        Previous Address (Required if less than 1 years at current address)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                          <label className={labelClass}>
                            Previous Street Address *
                          </label>
                          <input
                            type="text"
                            required={parseFloat(formData.timeAtAddress) < 2}
                            value={formData.previousStreetAddress}
                            onChange={(e) => handleInputChange('previousStreetAddress', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label className={labelClass}>
                            Previous City *
                          </label>
                          <input
                            type="text"
                            required={parseFloat(formData.timeAtAddress) < 2}
                            value={formData.previousCity}
                            onChange={(e) => handleInputChange('previousCity', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className={labelClass}>
                              Previous State *
                            </label>
                            <input
                              type="text"
                              required={parseFloat(formData.timeAtAddress) < 2}
                              maxLength="2"
                              value={formData.previousState}
                              onChange={(e) => handleInputChange('previousState', e.target.value.toUpperCase())}
                              className={inputClass}
                              placeholder="TX"
                            />
                          </div>
                          <div>
                            <label className={labelClass}>
                              Previous ZIP *
                            </label>
                            <input
                              type="text"
                              required={parseFloat(formData.timeAtAddress) < 2}
                              value={formData.previousZipCode}
                              onChange={(e) => handleInputChange('previousZipCode', e.target.value)}
                              className={inputClass}
                            />
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>
                            Time at Previous Address (Years) *
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            required={parseFloat(formData.timeAtAddress) < 2}
                            value={formData.previousTimeAtAddress}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                handleInputChange('previousTimeAtAddress', value);
                              }
                            }}
                            className={inputClass}
                            placeholder="e.g., 3.5"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Employment Information */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className={headingClass}>Employment Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>
                      Employer Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.employerName}
                      onChange={(e) => handleInputChange('employerName', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Employer Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.employerPhone}
                      onChange={(e) => handleInputChange('employerPhone', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Job Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.jobTitle}
                      onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Time Employed (Years) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      required
                      value={formData.timeEmployed}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleInputChange('timeEmployed', value);
                        }
                      }}
                      className={inputClass}
                      placeholder="e.g., 3.5"
                    />
                    <p className="text-xs text-gray-400 mt-1">Enter number of years</p>
                  </div>
                  <div>
                    <label className={labelClass}>
                      Monthly Income (Gross) *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.monthlyIncome}
                      onChange={(e) => handleInputChange('monthlyIncome', e.target.value)}
                      className={inputClass}
                      placeholder="$"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Additional Income
                    </label>
                    <input
                      type="number"
                      value={formData.additionalIncome}
                      onChange={(e) => handleInputChange('additionalIncome', e.target.value)}
                      className={inputClass}
                      placeholder="$"
                    />
                  </div>
                </div>
                {formData.additionalIncome && (
                  <div>
                    <label className={labelClass}>
                      Source of Additional Income
                    </label>
                    <input
                      type="text"
                      value={formData.incomeSource}
                      onChange={(e) => handleInputChange('incomeSource', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Vehicle & Loan Information */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className={headingClass}>Vehicle & Loan Information</h2>
                <div className="space-y-6">
                  <div>
                    <label className={labelClass}>
                      Vehicle of Interest
                    </label>
                    <input
                      type="text"
                      value={formData.vehicleInterest}
                      onChange={(e) => handleInputChange('vehicleInterest', e.target.value)}
                      className={inputClass}
                      placeholder="e.g., 2020 Toyota Camry"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Down Payment Amount
                    </label>
                    <input
                      type="number"
                      value={formData.downPayment}
                      onChange={(e) => handleInputChange('downPayment', e.target.value)}
                      className={inputClass}
                      placeholder="$"
                    />
                  </div>
                  
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.tradeIn}
                        onChange={(e) => handleInputChange('tradeIn', e.target.checked)}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-300">I have a trade-in</span>
                    </label>
                  </div>

                  {formData.tradeIn && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-700 rounded-lg border border-gray-600">
                      <div>
                        <label className={labelClass}>
                          Trade-In Year
                        </label>
                        <input
                          type="number"
                          value={formData.tradeInYear}
                          onChange={(e) => handleInputChange('tradeInYear', e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>
                          Trade-In Make
                        </label>
                        <input
                          type="text"
                          value={formData.tradeInMake}
                          onChange={(e) => handleInputChange('tradeInMake', e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>
                          Trade-In Model
                        </label>
                        <input
                          type="text"
                          value={formData.tradeInModel}
                          onChange={(e) => handleInputChange('tradeInModel', e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>
                          Trade-In Mileage
                        </label>
                        <input
                          type="number"
                          value={formData.tradeInMileage}
                          onChange={(e) => handleInputChange('tradeInMileage', e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelClass}>
                          Amount Owed on Trade-In (if any)
                        </label>
                        <input
                          type="number"
                          value={formData.tradeInPayoff}
                          onChange={(e) => handleInputChange('tradeInPayoff', e.target.value)}
                          className={inputClass}
                          placeholder="$"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Review & Submit */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className={headingClass}>Review & Submit</h2>
                
                <div className="bg-gray-700 rounded-lg p-6 space-y-4 border border-gray-600">
                  <h3 className="font-semibold text-lg text-white">Application Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="text-gray-100">
                      <span className="font-medium text-blue-400">Name:</span> {formData.firstName} {formData.lastName}
                    </div>
                    <div className="text-gray-100">
                      <span className="font-medium text-blue-400">Email:</span> {formData.email}
                    </div>
                    <div className="text-gray-100">
                      <span className="font-medium text-blue-400">Phone:</span> {formData.phone}
                    </div>
                    <div className="text-gray-100">
                      <span className="font-medium text-blue-400">Monthly Income:</span> ${formData.monthlyIncome}
                    </div>
                    <div className="text-gray-100">
                      <span className="font-medium text-blue-400">Employer:</span> {formData.employerName}
                    </div>
                    <div className="text-gray-100">
                      <span className="font-medium text-blue-400">Down Payment:</span> ${formData.downPayment || '0'}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      required
                      checked={formData.creditCheckConsent}
                      onChange={(e) => handleInputChange('creditCheckConsent', e.target.checked)}
                      className="w-4 h-4 text-primary-600 rounded mt-1"
                    />
                    <span className="text-sm text-gray-200">
                      I authorize Paradise-Performance to obtain my credit report and verify the information provided in this application.
                    </span>
                  </label>
                  
                  <label className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      required
                      checked={formData.termsAccepted}
                      onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                      className="w-4 h-4 text-primary-600 rounded mt-1"
                    />
                    <span className="text-sm text-gray-200">
                      I certify that all information provided is true and accurate to the best of my knowledge.
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="flex items-center px-6 py-3 border border-gray-600 rounded-lg font-semibold text-gray-200 hover:bg-gray-700 transition-colors"
                >
                  <FaArrowLeft className="mr-2" />
                  Previous
                </button>
              )}
              
              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="ml-auto flex items-center px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-lg disabled:opacity-50"
                >
                  Next
                  <FaArrowRight className="ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || !formData.creditCheckConsent || !formData.termsAccepted}
                  className="ml-auto px-8 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default FinanceApplication;
